// src/stores/useSquads.ts
import { create } from 'zustand'
import { supabase } from '../services/supabase'
import type { Squad, SquadMember, SquadTask, SquadSNREntry, SquadPresenceEntry } from '../types'

// ── Store interface ───────────────────────────────────────────────────────────

interface SquadsStore {
  squads: Squad[]
  activeSquadId: string | null
  members: SquadMember[]
  squadTasks: SquadTask[]
  snrLeaderboard: SquadSNREntry[]
  memberFocusTasks: { user_id: string; text: string; timer_running: boolean }[]
  /** Live presence rows from squad_presence table — drives Activity tab */
  squadPresence: SquadPresenceEntry[]
  loading: boolean

  load: () => Promise<void>
  setActiveSquad: (id: string) => void
  createSquad: (name: string) => Promise<void>
  joinSquad: (code: string) => Promise<void>
  leaveSquad: (squadId: string) => Promise<void>

  createSquadTask: (text: string, priority?: number) => Promise<void>
  assignSquadTask: (taskId: string, userId: string | null) => Promise<void>
  completeSquadTask: (taskId: string, done: boolean) => Promise<void>
  deleteSquadTask: (taskId: string) => Promise<void>
  toggleSquadTaskPriority: (taskId: string) => Promise<void>

  broadcastMySNR: (snrPct: number) => Promise<void>

  /**
   * Upsert the user's presence row — called when a focus timer starts.
   */
  broadcastPresence: (focusTask: string) => Promise<void>

  /**
   * Clear the user's presence row — called when a focus timer stops.
   */
  clearPresence: () => Promise<void>

  /** Internal: used by realtime.ts to reconcile squad task changes from other members */
  _patchTasks: (tasks: SquadTask[]) => void

  /** Internal: merge a single presence row update from realtime */
  _patchPresence: (row: SquadPresenceEntry) => void

  // Helpers (computed from members list)
  memberName: (userId: string | null) => string
  memberInitial: (userId: string | null) => string
  computeTeamSNR: () => { pct: number; signal: number; noise: number; total: number }
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useSquads = create<SquadsStore>((set, get) => ({
  squads: [],
  activeSquadId: null,
  members: [],
  squadTasks: [],
  snrLeaderboard: [],
  memberFocusTasks: [],
  squadPresence: [],
  loading: false,

  // ── Load ──────────────────────────────────────────────────────────────────

  load: async () => {
    set({ loading: true })
    try {
      // 1. Squads the user belongs to
      const { data: squads, error: sErr } = await supabase.from('squads').select('*')
      if (sErr) { console.error('[squads] load squads:', sErr); set({ loading: false }); return }

      const safeSquads = (squads ?? []) as Squad[]
      set({ squads: safeSquads })

      if (safeSquads.length === 0) {
        set({ members: [], squadTasks: [], snrLeaderboard: [], memberFocusTasks: [], squadPresence: [], loading: false })
        return
      }

      const squadId = get().activeSquadId ?? safeSquads[0].id
      set({ activeSquadId: squadId })

      const squadIds = safeSquads.map(s => s.id)

      // 2. Members — security-definer RPC to read auth.users names
      const { data: memberData, error: mErr } = await supabase.rpc(
        'get_squad_member_profiles',
        { p_squad_ids: squadIds }
      )
      if (mErr) {
        console.error('[squads] get_squad_member_profiles RPC:', mErr)
      }

      // Deduplicate by user_id
      const seen = new Set<string>()
      const members: SquadMember[] = ((memberData as any[]) ?? [])
        .filter(row => { if (seen.has(row.user_id)) return false; seen.add(row.user_id); return true })
        .map(row => ({
          user_id: row.user_id,
          squad_id: squadId,
          full_name: row.display_name ?? null,
          email: row.email ?? null,
          joined_at: row.joined_at ?? new Date().toISOString(),
        }))
      set({ members })

      // 3. Squad tasks for the active squad
      const { data: tasks, error: tErr } = await supabase
        .from('squad_tasks')
        .select('*')
        .eq('squad_id', squadId)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false })
      if (tErr) { console.error('[squads] load tasks:', tErr) }
      else { set({ squadTasks: (tasks ?? []) as SquadTask[] }) }

      // 4. SNR leaderboard
      const today = new Date().toISOString().split('T')[0]
      const { data: snrRows, error: lErr } = await supabase
        .from('squad_snr')
        .select('user_id, squad_id, snr_pct, snr_date')
        .in('squad_id', squadIds)
        .eq('snr_date', today)
        .order('snr_pct', { ascending: false })
      if (lErr) { console.error('[squads] load leaderboard:', lErr) }
      else {
        const currentMembers = get().members
        const leaderboard: SquadSNREntry[] = ((snrRows ?? []) as any[]).map(row => ({
          ...row,
          display_name: _memberName(currentMembers, row.user_id),
        }))
        set({ snrLeaderboard: leaderboard })
      }

      // 5. Member focus tasks (fallback for first load; presence takes over via realtime)
      if (members.length > 0) {
        const { data: focusTasks, error: fErr } = await supabase
          .from('tasks')
          .select('user_id, text, timer_running')
          .in('user_id', members.map(m => m.user_id))
          .eq('timer_running', true)
        if (fErr) { console.warn('[squads] load focus tasks:', fErr) }
        else { set({ memberFocusTasks: focusTasks ?? [] }) }
      }

      // 6. Squad presence rows
      const { data: presenceRows, error: pErr } = await supabase
        .from('squad_presence')
        .select('*')
        .in('squad_id', squadIds)
      if (pErr) { console.warn('[squads] load presence:', pErr) }
      else { set({ squadPresence: (presenceRows ?? []) as SquadPresenceEntry[] }) }
    } catch (err) {
      console.error('[squads] unexpected load error:', err)
    } finally {
      set({ loading: false })
    }
  },

  setActiveSquad: (id) => {
    set({ activeSquadId: id })
    get().load()
  },

  // ── Squad management ──────────────────────────────────────────────────────

  createSquad: async (name) => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) throw new Error('Not authenticated')

    const joinCode = Math.random().toString(36).substring(2, 8).toUpperCase()
    const { data: squad, error } = await supabase
      .from('squads')
      .insert({ name, join_code: joinCode })
      .select()
      .single()
    if (error) throw error

    await supabase.from('squad_members').insert({
      squad_id: (squad as Squad).id,
      user_id: session.user.id,
    })

    await get().load()
    set({ activeSquadId: (squad as Squad).id })
  },

  joinSquad: async (code) => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) throw new Error('Not authenticated')

    const { data: squadId, error: fErr } = await supabase.rpc('get_squad_by_code', {
      code: code.toUpperCase(),
    })
    if (fErr || !squadId) throw new Error('Invalid join code')

    const { error: jErr } = await supabase.from('squad_members').insert({
      squad_id: squadId,
      user_id: session.user.id,
    })
    if (jErr) throw jErr

    await get().load()
    set({ activeSquadId: squadId })
  },

  leaveSquad: async (squadId) => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) throw new Error('Not authenticated')

    const { error } = await supabase.from('squad_members')
      .delete()
      .eq('squad_id', squadId)
      .eq('user_id', session.user.id)
    if (error) throw error

    await get().load()
  },

  // ── Squad task mutations (local-first) ────────────────────────────────────

  createSquadTask: async (text, priority = 0) => {
    const squadId = get().activeSquadId
    if (!squadId) return

    const { data: { session } } = await supabase.auth.getSession()
    if (!session) { console.error('[squads] createSquadTask: no active session'); return }

    const { data, error } = await supabase
      .from('squad_tasks')
      .insert({ squad_id: squadId, created_by: session.user.id, text, priority, type: 'unclassified' })
      .select()
      .single()
    if (error) { console.error('[squads] createSquadTask:', error); return }
    set(s => ({ squadTasks: [data as SquadTask, ...s.squadTasks] }))
  },

  assignSquadTask: async (taskId, userId) => {
    const { error } = await supabase
      .from('squad_tasks')
      .update({ assigned_to: userId })
      .eq('id', taskId)
    if (error) { console.error('[squads] assignSquadTask:', error); return }
    set(s => ({
      squadTasks: s.squadTasks.map(t => t.id === taskId ? { ...t, assigned_to: userId } : t),
    }))
  },

  completeSquadTask: async (taskId, done) => {
    const doneAt = done ? new Date().toISOString() : null
    const { error } = await supabase
      .from('squad_tasks')
      .update({ done, done_at: doneAt })
      .eq('id', taskId)
    if (error) { console.error('[squads] completeSquadTask:', error); return }
    set(s => ({
      squadTasks: s.squadTasks.map(t => t.id === taskId ? { ...t, done, done_at: doneAt } : t),
    }))
  },

  deleteSquadTask: async (taskId) => {
    const { error } = await supabase.from('squad_tasks').delete().eq('id', taskId)
    if (error) { console.error('[squads] deleteSquadTask:', error); return }
    set(s => ({ squadTasks: s.squadTasks.filter(t => t.id !== taskId) }))
  },

  toggleSquadTaskPriority: async (taskId) => {
    const task = get().squadTasks.find(t => t.id === taskId)
    if (!task) return
    const newPriority = task.priority === 1 ? 0 : 1
    const { error } = await supabase
      .from('squad_tasks')
      .update({ priority: newPriority })
      .eq('id', taskId)
    if (error) { console.error('[squads] toggleSquadTaskPriority:', error); return }
    set(s => ({
      squadTasks: s.squadTasks.map(t => t.id === taskId ? { ...t, priority: newPriority } : t),
    }))
  },

  // ── SNR leaderboard broadcast ─────────────────────────────────────────────

  broadcastMySNR: async (snrPct) => {
    const squads = get().squads
    if (squads.length === 0) return

    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return

    const today = new Date().toISOString().split('T')[0]
    await Promise.all(
      squads.map(squad =>
        supabase.from('squad_snr').upsert(
          {
            user_id: session.user.id,
            squad_id: squad.id,
            snr_pct: snrPct,
            snr_date: today,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,squad_id,snr_date' }
        )
      )
    )
  },

  // ── Presence ──────────────────────────────────────────────────────────────

  broadcastPresence: async (focusTask) => {
    const squads = get().squads
    if (squads.length === 0) return

    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return

    const { useSNR } = await import('./useSNR')
    const snrPct = useSNR.getState().snrPct

    await Promise.all(
      squads.map(squad =>
        supabase.from('squad_presence').upsert(
          {
            user_id: session.user.id,
            squad_id: squad.id,
            focus_task: focusTask,
            snr_pct: snrPct,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,squad_id' }
        )
      )
    )
  },

  clearPresence: async () => {
    const squads = get().squads
    if (squads.length === 0) return

    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return

    await Promise.all(
      squads.map(squad =>
        supabase.from('squad_presence').upsert(
          {
            user_id: session.user.id,
            squad_id: squad.id,
            focus_task: null,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,squad_id' }
        )
      )
    )
  },

  // ── Internal helpers ───────────────────────────────────────────────────────

  _patchTasks: (tasks) => set({ squadTasks: tasks }),

  _patchPresence: (row) => {
    set(s => {
      const others = s.squadPresence.filter(
        p => !(p.user_id === row.user_id && p.squad_id === row.squad_id)
      )
      return { squadPresence: [...others, row] }
    })
  },

  // ── Computed helpers ──────────────────────────────────────────────────────

  memberName: (userId) => _memberName(get().members, userId),
  memberInitial: (userId) => _memberName(get().members, userId).charAt(0).toUpperCase(),

  computeTeamSNR: () => {
    const tasks = get().squadTasks
    const classified = tasks.filter(t => t.type !== 'unclassified')
    const signal = classified.filter(t => t.type === 'signal').length
    const noise = classified.filter(t => t.type === 'noise').length
    const total = classified.length
    const pct = total > 0 ? Math.round((signal / total) * 100) : 0
    return { pct, signal, noise, total }
  },
}))

// ── Private helpers ───────────────────────────────────────────────────────────

function _memberName(members: SquadMember[], userId: string | null): string {
  if (!userId) return 'Unassigned'
  const m = members.find(m => m.user_id === userId)
  if (!m) return 'Unknown'
  return m.full_name || (m.email ? m.email.split('@')[0] : 'Unknown')
}
